import { authApi } from '@/api/AuthApi'
import AuthError from '@/errors/AuthError'
import { logger } from '@/lib/logger'
import { useStore } from '@/store'
import { AuthLoginState } from '@/store/auth/AuthenticationStore'
import { FirebaseError } from 'firebase/app'
import { signInWithCustomToken } from 'firebase/auth'
import { firebaseAuth } from '#/firebase'

// Modify the handleLoginError function to only log the error
const handleLoginError = (error: unknown, errorCode: string, errorMessage: string) => {
	const loginError =
		error instanceof Error
			? new AuthError(errorMessage, errorCode, error)
			: new AuthError(errorMessage, errorCode)
	logger.error(loginError)
	updateLoginState(AuthLoginState.FAILED)
}

const updateLoginState = (loginState: AuthLoginState) => {
	useStore.getState().setLoginState(loginState)
}

export enum LoginCodeStatus {
	SUCCESSFUL = 0,
	RATE_LIMITED = 1,
	NOT_FOUND = 2,
}

export const sendLoginCodeToEmail = async (email: string): Promise<LoginCodeStatus> => {
	try {
		useStore.getState().setLoginEmail(email)
		const loginCodeResult = await authApi.sendLoginCodeToEmail(email)
		if (loginCodeResult?.isRateLimited) {
			return LoginCodeStatus.RATE_LIMITED
		}

		if (!loginCodeResult || !loginCodeResult.isCodeSent) {
			return LoginCodeStatus.NOT_FOUND
		}

		const loginState = loginCodeResult.isCodeSent
			? AuthLoginState.CODE_SENT
			: AuthLoginState.PENDING

		updateLoginState(loginState)
		return LoginCodeStatus.SUCCESSFUL
	} catch (error) {
		handleLoginError(error, 'failed_login_code_send', 'Failed to send login code')
		return LoginCodeStatus.NOT_FOUND // or another appropriate status
	}
}

export const exchangeLoginCodeForToken = async (
	code: string,
	onSuccess = () => {},
	onFailed = () => {}
): Promise<void> => {
	const loginEmail = useStore.getState().loginEmail

	if (!loginEmail) {
		updateLoginState(AuthLoginState.FAILED)
		onFailed()
		logger.error(new AuthError('Login email not found', 'missing_login_email'))
		return
	}

	try {
		const { token, isValid } = await authApi.exchangeCodeForAuthToken({
			email: loginEmail,
			code,
		})

		if (!isValid) {
			onFailed()
			return
		}

		await authUserFromCustomToken(token!, onSuccess, onFailed)
	} catch (error) {
		onFailed()
		handleLoginError(
			error,
			'failed_login_code_exchange',
			'Failed to exchange login code for token'
		)
	}
}

export const authUserFromCustomToken = async (
	customToken: string,
	onSuccess = () => {},
	onFailed = () => {}
): Promise<void> => {
	try {
		const authResult = await signInWithCustomToken(firebaseAuth, customToken)

		const authToken = await authResult.user.getIdToken()
		const tokenResult = await authResult.user.getIdTokenResult()

		useStore
			.getState()
			.authFromFirebaseUser(authToken, authResult.user, tokenResult.claims)

		onSuccess()
	} catch (error) {
		onFailed()
		if (error instanceof FirebaseError) {
			handleLoginError(
				error,
				`firebase_auth_error_${error.code}`,
				`Firebase authentication failed: ${error.message}`
			)
		} else {
			handleLoginError(
				error,
				'unknown_auth_error',
				'Unknown authentication error occurred'
			)
		}
	}
}
